/**
 * 施策画面 v2（plan.md §4）の表示用定数・純関数。API（Board）の値を画面の文言/色に写すだけで、
 * 判断（カードの有無・並び・数値）は worker 側がコードで決めている。ここでは再計算しない。
 */
import type { BoardAttention, BoardGroup, BoardHealth, BoardTone, SourcesState } from "../types/contract";
import { formatHistoryAt, formatMD, SOURCE_GROUP_LABEL, SOURCE_GROUP_ORDER } from "./mkRules";

export const SEVERITY_COLOR: Record<BoardAttention["severity"], string> = {
  high: "#a8442a",
  mid: "#c48f3a",
  idea: "#3a5fa8",
};

export const ATTENTION_KIND_LABEL: Record<BoardAttention["kind"], string> = {
  stale_wording: "期限文言",
  manual_overdue: "確認期限超過",
  meta_bad: "広告成果",
  wrapup: "終了後の確認",
  event_prep: "開始前の準備",
  sync_error: "連携エラー",
  idea: "提案",
};

export const HEALTH_LABEL: Record<Exclude<BoardHealth, null>, string> = {
  good: "好調",
  watch: "様子見",
  bad: "要注意",
  young: "判断保留（14日未満）",
};

export const TONE_COLOR: Record<Exclude<BoardTone, null>, string> = {
  good: "#2e7d5b",
  warn: "#a17223",
  bad: "#a8442a",
};

/** 判断カードに並べる対象名の最大数（超えた分は「ほかN件」）。 */
export const ATTENTION_ITEMS_MAX = 6;

/**
 * 片付いたもの行の理由表示。closed_reason が state_label（例「自動完了」）で始まっていればそのまま、
 * そうでなければ「状態：理由」。理由が無ければ state_label のみ。
 */
export function closedLabel(group: Pick<BoardGroup, "state_label" | "closed_reason">): string {
  const reason = group.closed_reason;
  if (!reason) return group.state_label;
  if (reason.startsWith(group.state_label)) return reason;
  return `${group.state_label}：${reason}`;
}

export interface SyncSummary {
  /** 'ok' = 全ソース正常（または未設定・未同期のみ）／'error' = 1つ以上エラー。 */
  state: "ok" | "error" | "never";
  /** 最後に同期できた時刻の表示（今日なら HH:MM、それ以外は M/D HH:MM）。無ければ null。 */
  at: string | null;
  /** state==='error' のソース名。 */
  errorLabels: string[];
}

/** ヘッダー右の「同期 ✓ 10:32」用。sources 3系統から最新の成功時刻と、エラーのソース名を出す。 */
export function summarizeSources(sources: SourcesState | null | undefined, today: string): SyncSummary {
  const errorLabels: string[] = [];
  let latest: string | null = null;
  for (const key of SOURCE_GROUP_ORDER) {
    const s = sources?.[key];
    if (!s) continue;
    if (s.state === "error") errorLabels.push(SOURCE_GROUP_LABEL[key]);
    if (s.last_synced_at && (!latest || s.last_synced_at > latest)) latest = s.last_synced_at;
  }
  let at: string | null = null;
  if (latest) {
    const full = formatHistoryAt(latest);
    const [md, hm] = full.split(" ");
    at = md === formatMD(today) && hm ? hm : full;
  }
  const state: SyncSummary["state"] = errorLabels.length > 0 ? "error" : latest ? "ok" : "never";
  return { state, at, errorLabels };
}
